/**
 * Applies the hidden set and the selection colour to every feature of every
 * visible tile content of one tileset. Cesium raises `tileVisible` for each
 * visible tile during the frame's traversal, before draw commands are built, so a
 * content stamped in that callback renders correctly in the same frame.
 *
 * Each content is stamped with the version it was last applied with; a bumped
 * version (update()) or an unstamped content (freshly loaded) triggers a re-apply.
 * No Cesium3DTileStyle is involved, so this never fights a style owner.
 */
import { Color } from 'cesium';
import type { Cesium3DTile, Cesium3DTileContent, Cesium3DTileset } from 'cesium';
import { IFC_COLOR_DEFAULT, IFC_COLOR_SELECTED } from './colors';

export class IfcVisibilityApplier {
	private version = 1;
	private hidden: Set<number> = new Set();
	private selected: number | null = null;
	private readonly stamped = new WeakMap<Cesium3DTileContent, number>();
	private readonly removeListener: () => void;
	private readonly requestRender: () => void;

	constructor(tileset: Cesium3DTileset, requestRender: () => void) {
		this.requestRender = requestRender;
		this.removeListener = tileset.tileVisible.addEventListener((tile: Cesium3DTile) =>
			this.applyToTile(tile)
		);
	}

	update(hidden: Set<number>, selected: number | null): void {
		this.hidden = hidden;
		this.selected = selected;
		this.version += 1;
		this.requestRender();
	}

	destroy(): void {
		this.removeListener();
	}

	private applyToTile(tile: Cesium3DTile): void {
		const content = tile.content;
		if (!content) return;
		const inner = (content as { innerContents?: Cesium3DTileContent[] }).innerContents;
		if (inner && inner.length > 0) inner.forEach((c) => this.applyToContent(c));
		else this.applyToContent(content);
	}

	private applyToContent(content: Cesium3DTileContent): void {
		if (this.stamped.get(content) === this.version) return;
		const count = content.featuresLength;
		for (let i = 0; i < count; i++) {
			const feature = content.getFeature(i);
			const id = feature.featureId;
			// Writing an unchanged value still dirties the batch texture, and a version
			// bump re-walks every feature — so compare first.
			const show = !this.hidden.has(id);
			if (feature.show !== show) feature.show = show;
			const color = id === this.selected ? IFC_COLOR_SELECTED : IFC_COLOR_DEFAULT;
			if (!Color.equals(feature.color, color)) feature.color = color;
		}
		this.stamped.set(content, this.version);
	}
}
